import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, Users, GraduationCap, BookOpen, Languages, ClipboardList, LogOut, Menu, Sparkles, Settings, BarChart3, Package, MessageCircle, TrendingUp, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Admin Pages
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminTutors from "@/pages/admin/AdminTutors";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminLanguages from "@/pages/admin/AdminLanguages";
import AdminAssignments from "@/pages/admin/AdminAssignments";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminResources from "@/pages/admin/AdminResources";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminStudentProgress from "@/pages/admin/AdminStudentProgress";
import AdminWaiverHistory from "@/pages/admin/AdminWaiverHistory";

const sidebarLinks = [
  { path: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
  { path: "/admin/students", label: "Students", icon: Users },
  { path: "/admin/progress", label: "Student Progress", icon: TrendingUp },
  { path: "/admin/waivers", label: "Waiver History", icon: ShieldCheck },
  { path: "/admin/tutors", label: "Tutors", icon: GraduationCap },
  { path: "/admin/courses", label: "Courses", icon: BookOpen },
  { path: "/admin/languages", label: "Languages", icon: Languages },
  { path: "/admin/assignments", label: "Assignments", icon: ClipboardList },
  { path: "/admin/resources", label: "Resources", icon: Package },
  { path: "/admin/messages", label: "Messages", icon: MessageCircle },
  { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/admin-vv-secret-access-2026");
  };

  return (
    <div className="admin-layout min-h-screen bg-[#FDFBF7]">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`admin-sidebar fixed md:sticky top-0 left-0 h-screen w-64 z-50 transform transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <span className="font-bold text-lg">VV Admin</span>
          </div>

          <nav className="space-y-2 flex-1">
            {sidebarLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  location.pathname === link.path ? 'bg-[#D6E6FA] border-2 border-[#0A0A0A]' : 'hover:bg-[#F0EFEB]'
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="border-t-2 border-[#0A0A0A]/10 pt-4">
            <div className="text-sm text-[#4A4A4A] mb-3">
              <span className="font-bold text-[#0A0A0A]">{user?.email}</span>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[#FF6B6B] font-medium">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </aside>

      <main className="admin-main-content flex-1">
        <header className="md:hidden sticky top-0 z-30 bg-[#FDFBF7] border-b-2 border-[#0A0A0A] p-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="p-2"><Menu className="w-6 h-6" /></button>
          <span className="font-bold">Admin</span>
          <div className="w-10" />
        </header>

        <div className="p-6 md:p-8">
          <Routes>
            <Route path="/dashboard" element={<AdminOverview />} />
            <Route path="/students" element={<AdminStudents />} />
            <Route path="/progress" element={<AdminStudentProgress />} />
            <Route path="/waivers" element={<AdminWaiverHistory />} />
            <Route path="/tutors" element={<AdminTutors />} />
            <Route path="/courses" element={<AdminCourses />} />
            <Route path="/languages" element={<AdminLanguages />} />
            <Route path="/assignments" element={<AdminAssignments />} />
            <Route path="/resources" element={<AdminResources />} />
            <Route path="/messages" element={<AdminMessages />} />
            <Route path="/analytics" element={<AdminAnalytics />} />
            <Route path="/settings" element={<AdminSettings />} />
            <Route path="*" element={<AdminOverview />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
